import { ReactNode } from 'react';

import { Link as MuiLink, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';

import CopyToClipboardButton from '../Clipboard/CopyToClipboardButton';
import { ResourceAttributes, getAttributeEntries } from './AttributeCountBadge';

type DetailTableProps = {
	children: ReactNode;
};

export function DetailTable({ children }: DetailTableProps) {
	return (
		<TableContainer component={Paper} variant="outlined" sx={{ flex: 1, minWidth: 0 }}>
			<Table size="small">
				<TableBody>{children}</TableBody>
			</Table>
		</TableContainer>
	);
}

export function DetailRow({ label, value }: { label: string; value: ReactNode }) {
	return (
		<TableRow>
			<TableCell component="th" scope="row" sx={{ width: 180, color: 'text.secondary' }}>
				{label}
			</TableCell>
			<TableCell sx={{ overflowWrap: 'anywhere' }}>{value || '-'}</TableCell>
		</TableRow>
	);
}

export function CopyableText({ value, title }: { value?: string; title: string }) {
	if (!value) {
		return null;
	}

	return (
		<Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
			<Typography variant="body2" sx={{ flex: 1, overflowWrap: 'anywhere', minWidth: 0 }}>
				{value}
			</Typography>
			<CopyToClipboardButton value={value} title={title} edge={false} sx={{ flexShrink: 0, ml: 'auto' }} />
		</Stack>
	);
}

export function CopyableLink({ value, title }: { value?: string; title: string }) {
	if (!value) {
		return null;
	}

	return (
		<Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, width: '100%' }}>
			<MuiLink href={value} target="_blank" rel="noreferrer" sx={{ flex: 1, overflowWrap: 'anywhere', minWidth: 0 }}>
				{value}
			</MuiLink>
			<CopyToClipboardButton value={value} title={title} edge={false} sx={{ flexShrink: 0, ml: 'auto' }} />
		</Stack>
	);
}

export function AttributesTable({ attributes }: { attributes: ResourceAttributes }) {
	const entries = getAttributeEntries(attributes);

	return (
		<DetailTable>
			<TableRow>
				<TableCell colSpan={2}>
					<Typography variant="subtitle2">Attributes</Typography>
				</TableCell>
			</TableRow>
			{entries.length === 0 ? (
				<TableRow>
					<TableCell colSpan={2} sx={{ color: 'text.secondary' }}>
						No attributes
					</TableCell>
				</TableRow>
			) : (
				entries.map(([key, value]) => (
					<TableRow key={key}>
						<TableCell component="th" scope="row" sx={{ width: 180, color: 'text.secondary', overflowWrap: 'anywhere' }}>
							{key}
						</TableCell>
						<TableCell sx={{ overflowWrap: 'anywhere' }}>
							<CopyableText value={value} title={`Copy ${key} to clipboard`} />
						</TableCell>
					</TableRow>
				))
			)}
		</DetailTable>
	);
}
